import React from 'react';
import { SerializedStyles } from '@emotion/react';
import Container from '@mui/material/Container';
import merge from 'lodash-es/merge';
import Slider, { Settings as SlickSettings } from 'react-slick';
import { i18n_see_all__v2 } from '@yi/i18n/phrases/i18n_see_all__v2';

import { utilities } from '~/common';
import { PartialOnKeys } from '~/types';

import { ReactComponent as ChevronRight } from './chevron-right.svg';
import { createSlickSettings } from './createSlickSettings';
import { Header, Section, Wrapper, styles } from './elements';
import { HeaderLink } from './HeaderLink';
import { Button } from '../../buttons';
import { useLocale } from '../../LocaleProvider';
import 'slick-carousel/slick/slick.scss';
import 'slick-carousel/slick/slick-theme.css';

export type BaseCarouselComposerProps<I extends Record<any, any>> = PartialOnKeys<
  BaseCarouselProps<I>,
  'getReactKey' | 'renderCard'
>;

export type SlickSliderAPI = {
  slickGoTo: (idx: number) => void;
};

export const DEFAULT_MAX_ITEMS = 12;

export interface BaseCarouselProps<I extends Record<any, any>> {
  items: I[];
  heading?: string;
  rows?: number;
  headerLink?: {
    text: string;
    url: string;
  };
  slickSettings?: SlickSettings;
  slickTrackHeight?: string;
  dark?: boolean;
  minItems?: number;
  maxItems?: number;
  preferences?: {};
  style?: React.CSSProperties;
  tailCard?: {
    showIfItemsLessThan: number;
    renderCard(): JSX.Element;
  };
  button?: { text: string; onClick(): void } | null;
  styleDotsAsLines?: boolean;
  lineWidthPercentage?: number;
  hasHoverableCards?: boolean;
  lazyLoad?: boolean;
  getReactKey(item: I): string | number;
  itemsTransform?(items: I[], other: {}): I[];
  onHeaderClick?(): void;
  // @TODO make abstract card class and use typeof its props here
  renderCard(cardProps: {
    item: I;
    lazyLoad?: boolean;
    onClick?(item: I): void;
  }): JSX.Element | null;
  onCarouselItemClick?(item: I): void;
  renderCarouselFilter?(): JSX.Element;
  renderHeadCard?(): JSX.Element;
  testId?: string;
  containerStyles?: React.CSSProperties;
  headerCSS?: SerializedStyles | {};
  overrideSlickCarouselCSS?: SerializedStyles | {};
  deprecatedEnableCarouselInvisibleCard?: boolean;
  className?: string;
  sliderRef?: (slider: Slider) => void;
}

const SeeAllText = () => {
  const { locale } = useLocale();
  return <>{i18n_see_all__v2(locale)}</>;
};

/**
 * RULES:
 * 1. variableWidth doesn't work correctly. To make it work correctly you should ALWAYS pass 'style' props with the specified width (currently it's implemented in a way that assume that the cards sizes are equal).
 * NOTE: slider has 2 variants of work: variableWidth when you can specify sizes of cards and without variableWidth, in this case width is calculated based on the container width and the slidesToShow
 *
 * lazy loading issues
 * if you need to render cards outside the container you have 2 options
 * 1. render more cards that container can render (slidesToShow). In this case the next button will misbehave (see canGoNext fn in the sources of library)
 * 2. use "progressive" lazyLoad. In this cases the slidesToShow will be rendered at the same time and next every second an additional slide will be rendered
 * NOTE: both of this options is relatively bad: (1) breaks behaviour, (2) render elements that we even don't need
 * TODO try to find proper workaround, maybe with use of intersectionObserver
 */
export class BaseCarousel<I extends Record<any, any>> extends React.PureComponent<
  BaseCarouselProps<I>
> {
  static defaultSlickSettings = createSlickSettings({
    sm: {
      slidesToShow: 2,
      slidesToScroll: 1,
      variableWidth: true,
      dots: false,
    },
    md: {
      slidesToShow: 3,
      slidesToScroll: 2,
      variableWidth: true,
      dots: false,
    },
    lg: {
      slidesToShow: 3,
      slidesToScroll: 2,
      dots: false,
    },
    xl: {
      slidesToShow: 4,
      slidesToScroll: 3,
      dots: false,
    },
    xxl: {
      slidesToShow: 4,
      slidesToScroll: 3,
      dots: false,
    },
  });
  static defaultProps = {
    minItems: 1,
    maxItems: DEFAULT_MAX_ITEMS,
    dark: false,
    slickSettings: {},
    heading: '',
    headerLink: undefined,
    tailCard: undefined,
    preferences: undefined,
    styleDotsAsLines: true,
    lineWidthPercentage: 100,
    button: undefined,
    onHeaderClick: undefined,
    itemsTransform: <T extends Record<any, any>>(items: T[]) => items,
    renderHeadCard: undefined,
    overrideSlickCarouselCSS: {},
    headerCSS: {},
    containerStyles: {},
    deprecatedEnableCarouselInvisibleCard: true,
  };
  slickSettings: SlickSettings;
  constructor(props: BaseCarouselProps<I>) {
    super(props);
    // Set slick settings to default, merged with any specified overwrites
    const { slickSettings } = props;
    this.slickSettings = merge({}, BaseCarousel.defaultSlickSettings, slickSettings);
  }

  renderCards(items: I[], lazyLoad?: boolean) {
    const {
      renderCard,
      onCarouselItemClick,
      getReactKey,
      tailCard,
      style,
      renderHeadCard,
      deprecatedEnableCarouselInvisibleCard,
    } = this.props;
    const cards = items
      .map(item => {
        const key = getReactKey(item);
        // react-slick requires wrapping div
        return (
          <div style={style} key={key}>
            {renderCard({ item, lazyLoad, onClick: onCarouselItemClick })}
          </div>
        );
      })
      .filter(v => v);

    if (renderHeadCard) {
      cards.unshift(<div key="CarouselHeadCard">{renderHeadCard()}</div>);
    }

    if (tailCard && items.length < tailCard.showIfItemsLessThan) {
      cards.push(<div key="CarouselTailCard">{tailCard.renderCard()}</div>);
    }

    // It's wrong solution caused by misuse of react-slick (it needed when you try to render more cards 'slidesToShow' than it's possible to render inside the continer)
    // use this flag until refactoring of the carousels to not use this feature (i.e. check each carousel one by one and be sure that slidesToShow corresponds to container width)
    if (deprecatedEnableCarouselInvisibleCard)
      cards.push(<div key="CarouselInvisibleCard" style={{ height: 'auto', width: '20px' }} />); // Add an invisible card to the end of every carousel to fix Slick not showing the last card

    return cards;
  }
  renderContent = (newItems: I[]) => {
    const defaultButton = {
      text: <SeeAllText />,
      onClick: this.props.onHeaderClick,
    };
    const {
      heading,
      headerLink,
      onHeaderClick,
      slickTrackHeight,
      button = defaultButton,
      lazyLoad,
      testId,
      overrideSlickCarouselCSS,
      headerCSS,
      lineWidthPercentage,
      styleDotsAsLines,
      renderCarouselFilter,
      sliderRef,
    } = this.props;

    const sliderRefProps = sliderRef ? { ref: sliderRef } : {};

    return (
      <Wrapper
        data-testid={testId}
        slickTrackHeight={slickTrackHeight}
        overrideSlickCarouselCSS={overrideSlickCarouselCSS}
        lineWidthPercentage={lineWidthPercentage}
        styleDotsAsLines={styleDotsAsLines}
      >
        {headerLink && <HeaderLink url={headerLink.url} text={headerLink.text} />}
        {heading && (
          <Header css={headerCSS}>
            <span
              onClick={onHeaderClick}
              role="button"
              data-testid={`app-slider-header-${utilities.formatKebabCase(heading)}`}
              aria-label={`View all ${heading}`}
              css={styles.header}
            >
              {heading}
            </span>

            {button && (
              <Button
                onClick={button.onClick}
                data-testid="app-slider-button"
                kind="text"
                css={styles.seeAllBtn}
                containerCss={styles.seeAllBtnContainer}
              >
                {button.text}
                <ChevronRight />
              </Button>
            )}
          </Header>
        )}
        {renderCarouselFilter && <div>{renderCarouselFilter()}</div>}
        <div>
          <Slider {...this.slickSettings} {...sliderRefProps}>
            {this.renderCards(newItems, lazyLoad)}
          </Slider>
        </div>
      </Wrapper>
    );
  };

  render() {
    const {
      heading,
      preferences,
      itemsTransform = BaseCarousel.defaultProps.itemsTransform,
      minItems = BaseCarousel.defaultProps.minItems,
      maxItems,
      items,
      dark,
      containerStyles,
    } = this.props;

    // Handle items transforms and min/max
    let newItems = itemsTransform(items, { preferences });
    if (!Array.isArray(newItems)) return null;

    newItems = newItems.slice(0, maxItems);
    if (newItems.length < minItems) {
      console.warn(`'${heading}'-carousel is hidden, because minItems requirement isn't met`);
      return null;
    }
    // To fix the slider jerk issue while switchiing from last to first slide.
    if (this.slickSettings?.slidesToScroll) {
      const remainder: boolean =
        newItems.length % this.slickSettings.slidesToScroll === 0 ? true : false;
      if (!remainder) this.slickSettings.slidesToScroll = 1;
    }

    return (
      // Hotjar fix for dynamic ids and class names: data-hj-ignore-attributes
      <Section dark={dark} data-hj-ignore-attributes className={this.props.className ?? ''}>
        <Container
          maxWidth="xl"
          data-testid={`app-slider-container-${utilities.formatKebabCase(heading)}`}
          style={containerStyles}
        >
          {this.renderContent(newItems)}
        </Container>
      </Section>
    );
  }
}
